import { useEffect } from "react";
import { ChevronRight, Plus, UserPlus, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import GluceraNavigationRoutes from "../utils/navigationRoutes";
import { useGroups } from "../hooks/useFamily";
import type { GroupListItem } from "../types/family";
import {
  measureActionLabel,
  measureBody,
  measureCardMeta,
  measureCardTitle,
  measureEyebrow,
  measurePrimaryButtonLabel,
  measureSectionTitle,
} from "../utils/measurePageTypography";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type FamilyGroupsSheetProps = {
  open: boolean;
  onClose: () => void;
  onCreateGroup: () => void;
};

const resolveGroupId = (group: GroupListItem) => group.group_id || group.id;

const FamilyGroupsSheet = ({ open, onClose, onCreateGroup }: FamilyGroupsSheetProps) => {
  const navigate = useNavigate();
  const { data: groupsData, isLoading, refetch } = useGroups({ enabled: open });

  const groups = (groupsData?.groups ?? []).filter((g) => {
    const gid = resolveGroupId(g);
    return gid && UUID_RE.test(gid);
  });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) refetch();
  }, [open, refetch]);

  if (!open) return null;

  const goToMembers = (group: GroupListItem) => {
    const gid = resolveGroupId(group);
    if (!gid) return;
    localStorage.setItem("family_group_id", gid);
    onClose();
    navigate(
      `/glucera${GluceraNavigationRoutes.GROUP_MEMBERS_LIST}?group_id=${encodeURIComponent(gid)}&group_name=${encodeURIComponent(group.name)}`,
    );
  };

  const goToAddMember = (group: GroupListItem) => {
    const gid = resolveGroupId(group);
    if (!gid) return;
    localStorage.setItem("family_group_id", gid);
    onClose();
    navigate(`/glucera${GluceraNavigationRoutes.INVITE_MEMBER}?group_id=${encodeURIComponent(gid)}`);
  };

  return (
    <div className="fixed inset-0 z-[200] flex flex-col justify-end" role="presentation">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden />

      <div className="relative z-10 mx-auto w-full max-w-[400px] duration-200 animate-in slide-in-from-bottom-4">
        <div className="max-h-[min(88dvh,_780px)] overflow-y-auto scrollbar-hide rounded-t-3xl border border-border bg-background shadow-2xl ring-1 ring-border/80">
          <div className="flex justify-center pb-2 pt-3">
            <div className="h-1 w-11 rounded-full bg-muted-foreground/25" aria-hidden />
          </div>

          <div className="flex items-start justify-between gap-3 px-5 pb-2">
            <div className="min-w-0">
              <p className={measureEyebrow}>Personalization · Family</p>
              <h2 className={`mt-1 ${measureSectionTitle}`}>Your health groups</h2>
              <p className={`mt-1 ${measureBody}`}>
                Pick a group to view members or add someone new.
              </p>
            </div>
            <button
              type="button"
              className={`mt-1 flex h-9 shrink-0 items-center gap-1 rounded-full bg-gradient-primary px-3.5 shadow-primary outline-none transition-[transform,opacity] hover:opacity-90 active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${measurePrimaryButtonLabel}`}
              onClick={() => {
                onClose();
                onCreateGroup();
              }}
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
              New
            </button>
          </div>

          <div className="space-y-3 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom,1rem))] pt-2">
            {isLoading &&
              [0, 1, 2].map((i) => (
                <div
                  key={`group-skel-${i}`}
                  className="h-[88px] animate-pulse rounded-2xl border border-border bg-muted/50"
                  aria-hidden
                />
              ))}

            {!isLoading && groups.length === 0 && (
              <div className="relative overflow-hidden rounded-3xl border border-border/80 bg-gradient-to-br from-card via-card to-[hsl(var(--primary-light))]/40 p-6 text-center shadow-sm backdrop-blur-sm dark:border-border/60">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full border border-[hsl(var(--info))]/25 bg-[hsl(var(--info-light))]">
                  <Users className="h-5 w-5 text-[hsl(var(--info))]" strokeWidth={1.85} aria-hidden />
                </div>
                <p className={`mt-3 ${measureCardTitle}`}>No groups yet</p>
                <p className={`mt-1 ${measureBody}`}>
                  Create a family group, then invite members.
                </p>
                <Button
                  type="button"
                  className="mt-4 h-11 rounded-2xl bg-gradient-primary px-6 text-sm font-semibold text-primary-foreground shadow-primary hover:opacity-90"
                  onClick={() => {
                    onClose();
                    onCreateGroup();
                  }}
                >
                  Create group
                </Button>
              </div>
            )}

            {!isLoading &&
              groups.map((group) => {
                const gid = resolveGroupId(group);
                const count = group.member_count ?? 0;
                const memberLabel = count === 1 ? "member" : "members";
                return (
                  <div
                    key={gid}
                    className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60"
                  >
                    <div
                      className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-primary/[0.06] to-transparent dark:from-primary/[0.1]"
                      aria-hidden
                    />
                    <button
                      type="button"
                      className="relative flex w-full items-center gap-3 p-4 text-left outline-none ring-offset-background transition-colors hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      onClick={() => goToMembers(group)}
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[hsl(var(--info))]/25 bg-[hsl(var(--info-light))]">
                        <Users className="h-[22px] w-[22px] text-[hsl(var(--info))]" strokeWidth={1.85} aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1 pt-0.5">
                        <span className="text-subhead font-bold leading-tight text-foreground">
                          {group.name}
                        </span>
                        <span className={`mt-1 block ${measureCardMeta}`}>
                          {count} {memberLabel}
                        </span>
                      </span>
                      <ChevronRight
                        className="h-5 w-5 shrink-0 text-muted-foreground sm:h-6 sm:w-6"
                        strokeWidth={2}
                        aria-hidden
                      />
                    </button>

                    <div className="border-t border-dashed border-border px-4" aria-hidden />

                    <button
                      type="button"
                        className={`flex w-full flex-col items-center gap-2 py-4 outline-none transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${measureActionLabel}`}
                      onClick={() => goToAddMember(group)}
                    >
                      <UserPlus className="h-5 w-5 text-muted-foreground" strokeWidth={1.85} aria-hidden />
                      Add member
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyGroupsSheet;
